/**
 * I198 (owner decision D56): a question the worker gave up on — its client was gone past
 * the presence grace (e.g. a phone backgrounded while the user fetched a secret), or its
 * ceiling passed — answers a late Submit with 409 QUESTION_EXPIRED. The card must not
 * vanish silently: the user just typed an answer and needs to know it went nowhere, and
 * — for a credential card whose key THIS submit saved to the keybook — that the key is
 * kept and a message continues the work.
 *
 * Returns the note's i18n key (namespace `agent`, rendered by MessageList) to show in
 * the card's place, or null when `err` is any OTHER failure (the card stays for a retry
 * and the caller rethrows). Matched on the route's CODE only — a bare 409 may be some
 * other refusal. `credentialSaved` is true only when this submit created or updated the
 * keybook entry and that write completed (CredentialPrompt reports it per path); an
 * existing entry merely picked or auto-resolved is not "saved".
 */
export type ExpiredQuestionNoteKey = 'question.expired' | 'question.expiredKeySaved'

export function questionExpiredNote(err: unknown, credentialSaved: boolean): ExpiredQuestionNoteKey | null {
  const code = (err as { code?: string } | null)?.code
  if (code !== 'QUESTION_EXPIRED') return null
  return credentialSaved ? 'question.expiredKeySaved' : 'question.expired'
}

/** I198 Codex round: 409 QUESTION_ANSWERED — another tab (or a double click) answered
 *  this question first; the card is simply dropped (the answer that won is in flight). */
export function questionAlreadyAnswered(err: unknown): boolean {
  return (err as { code?: string } | null)?.code === 'QUESTION_ANSWERED'
}

/** A note shown in place of a question card whose answer arrived after it expired. */
export interface ExpiredQuestionNotice {
  questionId: string
  noteKey: ExpiredQuestionNoteKey
}
